import qs from 'querystring';

import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import UserStore from 'stores/UserStore';

import { getConversationReceiver } from './utils';


const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);

const CONVERSATIONS_URL = `${BASE_URL}/loadConversations`;
const DELETE_CONVERSATION_URL = `${BASE_URL}/loadConversations?threadId={threadId}`;
const FILE_UPLOAD_URL = `${BASE_URL}/testCommunicatorFileUpload`;
const REPORT_ABUSE_URL = `${BASE_URL}/profiles/reportAbuse`;
const UNREAD_THREADS_URL = `${BASE_URL}/mail/unreadThreads`;
const MESSAGE_ACTIONS_URL = `${BASE_URL}/rest/v1.0/threads/{threadId}/messages/{messageId}/actions`;
const BAN_USER_URL = `${BASE_URL}/rest/v1.0/banlist/{username}`;


function getFilteredPreviews(items) {
  const { user: { userId, username } } = UserStore;

  // removes messages from "my fans"
  const filteredItems = items.filter((c) => (-1 * userId) !== c.threadId);

  return filteredItems.map((c) => {
    const updated = { ...c };

    for (const user in c.users) {
      if (c.users[user] !== username) {
        updated.receiverName = c.users[user];
      }
    }

    return updated;
  });
}

export async function fetchPreviews(filter = '') {
  let result = null;
  let error = false;

  try {
    const params = {
      mode: 'getThreads',
      limit: 999,
      filter,
    };

    result = await ApiService.get(CONVERSATIONS_URL, { params });
    result = getFilteredPreviews(result.data.conversations);
  } catch (e) {
    error = true;
  }

  return { result, error };
}

export async function fetchConversation(threadId, offset = 0) {
  const MESSAGES_LIMIT = 25;
  let result = null;
  let error = false;

  try {
    const params = {
      mode: 'loadConversation',
      threadId,
      offset,
      limit: MESSAGES_LIMIT,
    };

    result = await ApiService.get(CONVERSATIONS_URL, { params });
    result = result.data.thread;
    result.canLoadMore = result?.messages?.length >= MESSAGES_LIMIT;
    result.receiver = getConversationReceiver(result);
  } catch (e) {
    error = true;
  }

  return { result, error };
}

export async function postMessage(message) {
  let result = null;
  let error = false;

  try {
    const params = {
      data: qs.stringify(message),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    };

    await ApiService.post(CONVERSATIONS_URL, params);
    // might be a good idea to actually get the formatted message back, just sayin
    result = message;
  } catch (e) {
    error = true;
  }

  return { result, error };
}

export async function putConversation(receiver) {
  let result = null;
  let error = false;

  try {
    const { user: { username } } = UserStore;
    const params = { username: `${receiver},${username}` };

    result = await ApiService.put(CONVERSATIONS_URL, { params });
    result = result.data['thread_id'];
  } catch (e) {
    error = true;
  }

  return { result, error };
}

export async function postReportAbuse(data) {
  let result = null;
  let error = false;

  try {
    const params = {
      data: qs.stringify(data),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    };

    result = await ApiService.post(REPORT_ABUSE_URL, params);
    result = result.data;
  } catch (e) {
    error = true;
  }

  return { result, error };
}

export async function postBanUser(receiverName) {
  let result = null;
  let error = false;

  try {
    const { user: { username } } = UserStore;
    const url = ApiService.substitute(
      BAN_USER_URL,
      { username }
    );

    const params = { bannedUser: receiverName };

    result = await ApiService.post(url, { params });
  } catch (e) {
    error = true;
  }

  return { result, error };
}

export async function deleteConv(threadId) {
  let result = null;
  let error = false;

  try {
    const url = ApiService.substitute(
      DELETE_CONVERSATION_URL,
      { threadId }
    );

    result = await ApiService.delete(url);
    result = result.data;
  } catch (e) {
    error = true;
  }

  return { result, error };
}

export async function markAsRead(threadId) {
  let result = null;
  let error = false;

  try {
    const params = {
      data: qs.stringify({ threadId }),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    };

    result = await ApiService.post(UNREAD_THREADS_URL, params);
  } catch (e) {
    error = true;
  }

  return { result, error };
}

export async function postFile(file) {
  let result = null;
  let error = false;

  try {
    const { user: { accessHash, username } } = UserStore;
    const { size, type, name } = file;

    const extensionMatch = name.match(/[^.]*$/);
    const data = {
      size,
      contentType: type,
      extension: extensionMatch[0] || '',
      username,
      accessHash,
    };

    const params = {
      data: qs.stringify(data),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    };

    result = await ApiService.post(FILE_UPLOAD_URL, params);
    result = result.data;
  } catch (e) {
    error = true;
  }

  return { result, error };
}

export async function postMarkAsSpam({ threadId, messageId }) {
  let result = null;
  let error = false;

  try {
    const { user: { username } } = UserStore;

    const url = ApiService.substitute(
      MESSAGE_ACTIONS_URL,
      { threadId, messageId }
    );

    const data = {
      username,
      action: 'REPORT_AS_SPAM',
    };

    const params = {
      data: JSON.stringify(data),
      headers: { 'content-type': 'application/json; charset=utf-8' },
    };

    result = await ApiService.post(url, params);
  } catch (e) {
    error = true;
  }

  return { result, error };
}

export async function postDeleteMessage({ threadId, messageId }) {
  let result = null;
  let error = false;

  try {
    const { user: { username } } = UserStore;

    const url = ApiService.substitute(
      MESSAGE_ACTIONS_URL,
      { threadId, messageId }
    );

    const data = {
      username,
      action: 'DELETE_MESSAGE',
    };

    const params = {
      data: JSON.stringify(data),
      headers: { 'content-type': 'application/json; charset=utf-8' },
    };

    result = await ApiService.post(url, params);
  } catch (e) {
    error = true;
  }

  return { result, error };
}
